import { ChatRole, setIsOverlayLogin, useAppDispatch, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonVariant } from "@next-platform/client-common-spicy-ui";
import { Skeleton } from "@nextui-org/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import ImageThumbnailBlurred from "@/components/AvatarGeneratorModal/components/ImageGallery/ImageThumbnailBlurred";
import Markdown from "@/components/common/Markdown";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import { useMediaStore } from "@/hooks";
import useAuth from "@/services/useAuth";
import { cn, replaceMessageTokens } from "@/utils";

import { ChatBubbleImage } from "./ChatBubbleImage";
import { ChatBubbleContentProps } from "./types";

const SKELETON_CLASSNAME = "h-[18px] rounded-sm !bg-gray-9";

// This component is used to render different chat box states such as typing state, editing message state, normal text/image message state.
const ChatBubbleContent = ({
  message,
  userName,
  characterName,
  isAvatarImageBlurred,
  isSelected,
  showCheckbox,
  isImageNsfwGatingEnabled,
  isTextNsfwGatingEnabled,
  textNsfwCtaMode,
  imageNsfwCtaMode,
  onOpenImageDetails,
}: ChatBubbleContentProps) => {
  const navigate = useNavigate();
  const isMobile = useMediaStore((store) => store.isMobile);
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isUserMessage = message?.role === ChatRole.USER;
  const markDownClassNames = useMemo(() => {
    const emNonSelectedTextColor = isUserMessage ? "text-sky-6 dark:text-sky-7" : "text-sky-10 dark:text-sky-6";
    return {
      em: isSelected ? "!text-white" : emNonSelectedTextColor,
      para: isSelected ? "!text-white" : cn(isUserMessage && "text-white !dark:text-white"),
      textPrimary: isSelected || isUserMessage ? "!text-white" : "!text-black dark:!text-white",
    };
  }, [isSelected, isUserMessage]);

  const displayLoginOverlay = () => {
    dispatch(setIsOverlayLogin(true));
  };

  const handleAgeVerification = () => {
    if (isAuthenticated) {
      navigate(ROUTES.ageVerification);
    } else {
      displayLoginOverlay();
    }
  };

  const handleSubscribe = () => {
    navigate(ROUTES.subscribe);
  };

  if (!message) return null;
  const imageUrl = message?.image_url;

  const AgeVerificationButton = ({ className = "" }: { className?: string }) => (
    <Button variant={ButtonVariant.INVERSE} className={className} onClick={handleAgeVerification}>
      {isAuthenticated ? t("chat:chatBubble.cta.verifyYourAge") : t("chat:chatBubble.cta.signUpToView")}
    </Button>
  );

  const SubscribeButton = ({ className = "" }: { className?: string }) => (
    <Button variant={ButtonVariant.INVERSE} className={className} onClick={isAuthenticated ? handleSubscribe : displayLoginOverlay}>
      {isAuthenticated ? <Trans i18nKey="chat:chatBubble.cta.subscribeToView" /> : <Trans i18nKey="chat:chatBubble.cta.signInOrRegisterToView" />}
    </Button>
  );

  if (isImageNsfwGatingEnabled) {
    return <ImageThumbnailBlurred variant="conversation" hideCta={imageNsfwCtaMode === "disabled"} />;
  }

  if (isTextNsfwGatingEnabled) {
    return (
      <div className="flex flex-col gap-md">
        <Skeleton className={cn(SKELETON_CLASSNAME, "mr-sm")} />
        <Skeleton className={SKELETON_CLASSNAME} />
        {isMobile ? (
          <>
            <Skeleton className={cn(SKELETON_CLASSNAME, "mr-3")} />
            <Skeleton className={cn(SKELETON_CLASSNAME, "mr-7")} />
            <Skeleton className={cn(SKELETON_CLASSNAME, "mr-3")} />
          </>
        ) : null}
        {textNsfwCtaMode === "paywall" && <SubscribeButton className="w-fit" />}
        {textNsfwCtaMode === "verifyAge" && <AgeVerificationButton className="w-fit" />}
      </div>
    );
  }

  if (imageUrl) {
    // having imageUrl mean the image can be displayed
    // if an image is not supposed to be seen by the user then imageUrl will be undefined
    return (
      <div className="flex flex-col relative overflow-hidden rounded-md">
        <ChatBubbleImage
          imageUrl={imageUrl}
          alt={characterName}
          isAvatarImageBlurred={isAvatarImageBlurred}
          isUserMessage={isUserMessage}
          showCheckbox={showCheckbox}
          onOpenDetails={onOpenImageDetails}
        />
        <Markdown classNames={markDownClassNames} isCommandBarVisible={isUserMessage}>
          {replaceMessageTokens(message?.content, {
            characterName: characterName,
            userName: userName,
          })}
        </Markdown>
      </div>
    );
  }

  return (
    <Markdown classNames={markDownClassNames} isCommandBarVisible={isUserMessage}>
      {replaceMessageTokens(message?.content, {
        characterName: characterName,
        userName: userName,
      })}
    </Markdown>
  );
};

export default ChatBubbleContent;
